/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './IoTImageMapper';

const IoTImageMapperManager: ComponentManagerFn<'IoTImageMapper'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'IoTImageMapper'>(platform, 'IoTImageMapper'),
    type: 'IoTImageMapper',
    name: 'IoT Image Mapper',
    isChildAllowed: (page, item, childType, { appUid, pageId }) => {
      if (
        !(
          [
            'Area',
            // 'Device',
            'ExpressionWatchListener',
            // 'Popover',
            // 'Pulse',
            'PulseTip',
            // 'Tracker',
            'AreaToolbar',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      if (
        (
          ['Area', 'Device', 'Popover', 'Pulse', 'Tracker'] as ItemType[]
        ).includes(childType)
      ) {
        const children = platform.getChildrenOfType(
          appUid,
          pageId,
          page,
          item,
          childType,
        );
        return children.length === 0 ? true : 'Already added';
      }
      return true;
    },
    defaultValue,
    props: {
      imageSrc: {
        label: 'Image Src',
        type: 'expressionOnly',
        returnType: () => 'string',
      },
      imageWidth: {
        label: 'Image Width',
        type: 'expressionOnly',
        returnType: () => 'number',
      },
      imageHeight: {
        label: 'Image Height',
        type: 'expressionOnly',
        returnType: () => 'number',
      },
      scale: {
        label: 'Initial Zoom Level',
        type: 'number',
        min: 0.1,
        max: 20,
        step: 0.1,
      },
      usePublicUrl: {
        label: 'Use Public URL',
        type: 'boolean',
        helpText:
          'When using Attachment File Id, check this to use a public URL instead of authenticated URL to allow browser to cache the image. The dataSource must have "Public Download Allowed" access checked for public role.',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
      },
      hideZoomButtons: {
        label: 'Hide Zoom Buttons',
        type: 'boolean',
      },
      hideZoomLevel: {
        label: 'Hide Zoom Level Indicator',
        type: 'boolean',
      },
      doubleClickToZoom: {
        label: 'Double Click To Zoom',
        type: 'boolean',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'hideZoomLevel':
          return !itemData.hideZoomButtons;
        default:
          return true;
      }
    },
    canHaveChildren: () => true,
    category: 'IoT',
    icon: { iconName: 'draw-polygon' },
    description: `**Overview**<br/>
The IoT Image Mapper is a sophisticated canvas component used to visualize spatial data on top of static images. It serves as the foundation for creating interactive floor plans, facility maps, or technical schematics. By overlaying dynamic zones (**Area**) and markers (**Device**) onto a background image, it transforms a simple picture into a live, data-driven dashboard.
Use this component to build interfaces for "Smart Building" management, warehouse inventory tracking, or industrial monitoring systems.

**1. Background Image**<br/>
The core of the mapper is the image it displays, which serves as the coordinate reference for all children.
* **Image Src:** An expression returning the URL or Attachment ID of the background image (e.g., a PNG floor plan or SVG schematic).
* **Dimensions:** Defining **Image Width** and **Image Height** is critical to ensure the coordinate system for overlay items (Areas and Devices) aligns correctly with the visual image.
* **Use Public URL:** Optimizes performance by using a publicly cacheable URL for the image asset.

**2. View & Navigation Controls**<br/>
You can customize how users navigate and interact with the canvas.
* **Initial Zoom (scale):** Sets the default magnification level when the map loads (0.1x to 20x).
* **Zoom Controls:**
    * **Hide Zoom Buttons:** Removes the +/- UI controls from the canvas.
    * **Hide Zoom Level Indicator:** Removes the text showing the current zoom percentage (e.g., "150%").
    * **Double Click To Zoom:** Enables standard map behavior where double-clicking a point zooms in automatically.

**3. Layers & Interactivity**<br/>
The IoT Image Mapper is a container that gains functionality through its specific child components.
* **Layers:** You add children to visualize different data types:
    * **Area:** Draws polygon zones (e.g., rooms, storage bays).
    * **Device:** Plots individual points or icons (e.g., sensors, cameras).
* **Toolbar:** Add an **AreaToolbar** child to provide interactive editing controls such as Draw, Save, and Undo.
* **Permissions:** Use the **Updatable** boolean expression to control whether the map is in **Edit Mode** (where users can modify shapes) or **View Mode** (read-only interactive dashboard).

**4. Best Use Cases**<br/>
* **Facility Management:** Uploading a blueprint and drawing "Zones" to track occupancy sensors or temperature in real-time.
* **Parking Management:** Visualizing a parking lot map where individual spots change color (Red/Green) based on sensor-reported availability.
* **Security Dashboard:** Placing camera icons on a building layout that open live video feeds when clicked.
* **Retail Analytics:** Creating a heatmap of customer traffic flow overlaid on a physical store layout.`,
  };
};

export default IoTImageMapperManager;
